import { ACCESSORIES_ROOT_PATH, POP_PATHNAME } from '@/app/consts/pathnames';
import { ChargerModelEnum } from '@/app/enums/ChargerModelEnum';
import { RingModelEnum } from '@/app/enums/RingModelEnum';
import { type RingStyleEnum } from '@/app/enums/RingStyleEnum';
import type { RingFinish, RingStyle } from '@/app/types/ring';

import { SELECT_SIZE, SIZING_KIT } from '../_consts';
import type CurrentRouteValues from '../_hooks/useCurrentRoute/types';
import type {
  GetStyleStockStatus,
  IsOuraRing4,
} from '../_hooks/useProductsInventory';

interface CheckCurrentRouteReturn {
  isValid: boolean;
  redirectUrl: string | null;
}

interface CheckCurrentRouteProps extends CurrentRouteValues {
  getFirstStockedFinish: (props: GetStyleStockStatus) => RingFinish;
  getFirstStockedStyle: (props: IsOuraRing4) => RingStyle;
}

interface RouteValue {
  isValid: boolean;
  value: string;
}

const _checkAccessoryRoutes = (
  accessory: RouteValue,
  size: RouteValue,
): CheckCurrentRouteReturn => {
  if (accessory.value.length && !accessory.isValid) {
    return {
      isValid: false,
      redirectUrl: `${ACCESSORIES_ROOT_PATH}/${ChargerModelEnum.ChargerOura4}`,
    };
  }

  if (size.value.length && !size.isValid && accessory.isValid) {
    return {
      isValid: false,
      redirectUrl: `${ACCESSORIES_ROOT_PATH}/${accessory.value}/${SELECT_SIZE}`,
    };
  }

  return {
    isValid: true,
    redirectUrl: null,
  };
};

const _checkOR4WizardRoutes = ({
  finish,
  firstStockedFinish,
  size,
}: {
  finish: RouteValue;
  firstStockedFinish: RingFinish;
  size: RouteValue;
}): CheckCurrentRouteReturn => {
  if (finish.value.length && !finish.isValid) {
    return {
      isValid: false,
      redirectUrl: `${POP_PATHNAME}/${RingModelEnum.Oura4}/${firstStockedFinish}`,
    };
  }

  if (size.value.length && !size.isValid) {
    return {
      isValid: false,
      redirectUrl: `${POP_PATHNAME}/${RingModelEnum.Oura4}/${finish.value}/${SIZING_KIT}`,
    };
  }

  return {
    isValid: true,
    redirectUrl: null,
  };
};

const _checkRingsRoutes = ({
  finish,
  getFirstStockedFinish,
  getFirstStockedStyle,
  isOuraRing4Wizard,
  size,
  style,
}: Pick<
  CheckCurrentRouteProps,
  | 'finish'
  | 'getFirstStockedFinish'
  | 'getFirstStockedStyle'
  | 'isOuraRing4Wizard'
  | 'size'
  | 'style'
>): CheckCurrentRouteReturn => {
  const firstStockedStyle = getFirstStockedStyle({
    isOuraRing4: isOuraRing4Wizard,
  });
  const firstStockedFinish = getFirstStockedFinish({
    isOuraRing4: isOuraRing4Wizard,
    style: style.value as RingStyleEnum,
  });

  if (isOuraRing4Wizard) {
    return _checkOR4WizardRoutes({
      finish,
      firstStockedFinish,
      size,
    });
  } else {
    if (style.value.length && !style.isValid) {
      return {
        isValid: false,
        redirectUrl: `${POP_PATHNAME}/${RingModelEnum.Gen3}/${firstStockedStyle}`,
      };
    }

    if (finish.value.length && !finish.isValid) {
      return {
        isValid: false,
        redirectUrl: `${POP_PATHNAME}/${RingModelEnum.Gen3}/${style.value}/${firstStockedFinish}`,
      };
    }

    if (size.value.length && !size.isValid) {
      return {
        isValid: false,
        redirectUrl: `${POP_PATHNAME}/${RingModelEnum.Gen3}/${style.value}/${finish.value}/${SIZING_KIT}`,
      };
    }
    return {
      isValid: true,
      redirectUrl: null,
    };
  }
};

const checkCurrentRoute = ({
  accessory,
  finish,
  getFirstStockedFinish,
  getFirstStockedStyle,
  isAccessory,
  isOuraRing4Wizard,
  size,
  style,
}: CheckCurrentRouteProps): CheckCurrentRouteReturn => {
  if (isAccessory) {
    return _checkAccessoryRoutes(accessory, size);
  } else {
    return _checkRingsRoutes({
      finish,
      getFirstStockedFinish,
      getFirstStockedStyle,
      isOuraRing4Wizard,
      size,
      style,
    });
  }
};

export default checkCurrentRoute;
